import { useState, useEffect } from "react";
import { Box, Button, Grid, Menu, MenuItem, TextField } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import DashboardWidgets from "./DashboardWidgets";
import { formatCurrentDate } from "../../shared/helper";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReusableSnackbar from "../../shared/Alert/ReusableSnackbar";
import ConfirmDialog from "../../shared/ConfirmDialog/ConfirmDialog";
import api from "../../services/api";

export default function Dashboard({
  therapyPlans,
  getPatientTherapyRecord,
  userDetail,
}) {
  const [open, setOpen] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [presentPatient, setPresentPatient] = useState(false);
  const [absentPatient, setAbsentPatient] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState({
    status: false,
    message: "",
    severity: "",
  });
  const [tempDetails, setTempDetails] = useState({
    location: "",
    temp: "",
    today_appointments: 0,
    active_patients: 0,
    new_patients: 0,
    total_patients: 0,
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const branchopen = Boolean(anchorEl);
  const [noteText, setNoteText] = useState("");
  const handleOpenDialog = () => setOpen(true);
  const handleCloseDialog = () => setOpen(false);
  const handleSnackClose = () => setSnackbarOpen(false);
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const DEFAULT_IMAGE =
    BASE_URL + "/static/profile_pic/default_profile_pic.png";

  const getPatientStats = async () => {
    try {
      const response = await api.get("app/dashboard/stats", {
        params: {
          branch_id: selectedBranch?.id, // send as query param
        },
      });

      setTempDetails((prev) => ({
        ...prev,
        today_appointments: response.data.todays_appointments,
        active_patients: response.data.active_patients,
        new_patients: response.data.new_patients,
        total_patients: response.data.total_patients,
      }));
    } catch (err) {
      console.error("Error fetching stats", err);
    }
  };

  useEffect(() => {
    if (userDetail?.branches?.length > 0 && !selectedBranch) {
      setSelectedBranch(userDetail.branches[0]); // ✅ first record as default
    }
    getCurrentTemp();
  }, [userDetail, selectedBranch]);

  useEffect(() => {
    if (selectedBranch) {
      getPatientTherapyRecord(selectedBranch?.id);
      getPatientStats();
    }
  }, [selectedBranch]);

  const getCurrentTemp = async () => {
    try {
      const response = await api.get(
        `http://api.weatherapi.com/v1/current.json`,
        {
          params: {
            key: "fa9943c1cc2148ba8ec101942251206",
            q: "Bhubaneswar",
          },
        },
      );
      setTempDetails((prev) => ({
        ...prev,
        location: response.data.location.name,
        temp: response.data.current.temp_c,
      }));
    } catch (error) {
      console.error("Error fetching weather:", error);
    }
  };

  const hanldlePatietPresent = (id) => {
    setSelectedRecordId(id);
    setPresentPatient(true);
    setAbsentPatient(false);
    handleOpenDialog();
  };
  const hanldleAbsentPresent = (id) => {
    setSelectedRecordId(id);
    setPresentPatient(false);
    setAbsentPatient(true);
    handleOpenDialog();
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (branch) => {
    if (branch) {
      setSelectedBranch(branch);
    }
    setAnchorEl(null);
  };
  const hanldleAgreePresent = async () => {
    const id = selectedRecordId;
    if (!id) return;

    if (presentPatient === false) {
      if (noteText.trim() === "") {
        setSnackbarOpen({
          status: true,
          message: "Please provide a reason for absence.",
          severity: "error",
        });
        return; // Only return if note is missing
      }
    }
    const statusPayload = {
      attended: presentPatient ? true : false,
      notes: presentPatient ? "Present" : noteText,
    };
    try {
      const response = await api.post(
        `app/patient/therapy_plans/${id}/sessions/`,
        statusPayload,
      );
      if (response.status === 200) {
        setSnackbarOpen({
          status: true,
          message: presentPatient
            ? "Patient Marked as Present!"
            : "Patient Marked as Absent!",
        });
        getPatientTherapyRecord(selectedBranch?.id);
      } else {
        setSnackbarOpen({
          status: true,
          message: "something went wrong! Please try again.",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbarOpen({
        status: true,
        message: error?.response?.data?.detail || "An error occurred",
        severity: "error",
      });
    } finally {
      handleCloseDialog();
      setSelectedRecordId(null);
    }
  };

  const columns = [
    {
      field: "profile_pic_path",
      headerName: "",
      flex: 0.5, // small proportional space
      minWidth: 80,
      sortable: false,
      editable: false,
      renderCell: (params) => {
        const imagePath = params.row.profile_pic_path
          ? `${BASE_URL}${params.row.profile_pic_path}`
          : DEFAULT_IMAGE;

        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <img
              src={imagePath}
              alt="Profile"
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                objectFit: "cover",
              }}
              onError={(e) => {
                e.target.onerror = null; // avoid infinite loop
                e.target.src = DEFAULT_IMAGE;
              }}
            />
          </div>
        );
      },
    },
    {
      field: "patient_name",
      headerName: "Patient Name",
      flex: 1, // expands to fill available space
      editable: false,
    },
    {
      field: "service_name",
      headerName: "Service",
      flex: 1,
      editable: false,
    },
    {
      field: "total_sessions",
      headerName: "Total Sessions",
      flex: 1,
      editable: false,
    },
    {
      field: "frequency_per_week",
      headerName: "Frequency Per Week",
      flex: 1,
      editable: false,
    },
    {
      field: "sessions_attended",
      headerName: "Sessions Attended",
      flex: 1,
      editable: false,
    },
    {
      field: "therapy_days",
      headerName: "Therapy Days",
      flex: 1,
      editable: false,
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1.3,
      editable: false,
      sortable: false,
      renderCell: (param) => {
        const isDisable = param.row.notes !== null;
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
              gap: "10px",
            }}
          >
            <Button
              size="small"
              key="present"
              className={`attenadance_btn${
                param.row.attended === true && param.row.notes === "Present"
                  ? "present"
                  : ""
              }`}
              onClick={() => {
                hanldlePatietPresent(param.row.record_id);
              }}
              disabled={isDisable}
            >
              Present
            </Button>
            <Button
              size="small"
              key="absent"
              className={`attenadance_btn${
                param.row.attended === false && param.row.notes !== null
                  ? "absent"
                  : ""
              }`}
              onClick={() => {
                hanldleAbsentPresent(param.row.record_id);
              }}
              disabled={isDisable}
            >
              {" "}
              Absent
            </Button>
          </div>
        );
      },
    },
  ];
  const filteredRows = Array.isArray(therapyPlans)
    ? therapyPlans?.filter((row) =>
        Object.values(row).some(
          (val) =>
            val &&
            val.toString().toLowerCase().includes(searchQuery.toLowerCase()),
        ),
      )
    : [];
  return (
    <>
      <Box sx={{ flexGrow: 1 }}>
        <Grid container spacing={2}>
          <Grid size={8}>
            <h1 className="dashboard_heading">Welcome {userDetail?.name}</h1>
            <h5 className="dashboard_subheading">
              Hope you are doing well! Happy to see you again
            </h5>
          </Grid>
          <Grid size={4}>
            <Box className="dashbaord_dateBox">
              <Button
                aria-controls={branchopen ? "branch-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={branchopen ? "true" : undefined}
                onClick={handleClick}
                className="date_button"
                boxshadow={3}
                endIcon={<ExpandMoreIcon />}
              >
                {selectedBranch ? selectedBranch?.name : "Select Branch"}
              </Button>

              <Menu
                id="branch-menu"
                anchorEl={anchorEl}
                open={branchopen}
                onClose={() => handleClose(null)}
              >
                {userDetail?.branches?.map((branch) => (
                  <MenuItem key={branch.id} onClick={() => handleClose(branch)}>
                    {branch.name}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <DashboardWidgets
        tempDetails={tempDetails}
        currentDate={formatCurrentDate()}
      />

      <Box
        className="tabcontent_box"
        sx={{ padding: "25px", minWidth: "unset" }}
      >
        <p className="table-title">Todays Appoinment's</p>

        <p className="table-description">
          This section shows all patient appointments scheduled for today along
          with their therapy details. Use the action buttons to record
          attendance.
        </p>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <TextField
            size="small"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </Box>
        <Box sx={{ height: 400, width: "100%" }}>
          <DataGrid
            sx={{
              fontFamily: "Nunito, sans-serif",
              // Cell (row content) font size
              "& .MuiDataGrid-cell": {
                fontSize: "12px",
              },

              // Header font size
              "& .MuiDataGrid-columnHeaderTitle": {
                fontSize: "13px",
                fontWeight: "bold",
              },
            }}
            rows={filteredRows}
            getRowId={(row) => row.patient_id}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 5,
                },
              },
            }}
            pageSizeOptions={[5]}
            disableRowSelectionOnClick
          />
        </Box>
      </Box>
      <ConfirmDialog
        open={open}
        title={presentPatient ? "Confirm Present" : "Confirm Absent"}
        description={
          presentPatient
            ? "Are you sure you want to mark the Patient as Present? This action cannot be undone."
            : "Are you sure you want to mark the Patient as Absent? This action cannot be undone."
        }
        presentPatient={presentPatient}
        absentPatient={absentPatient}
        agreeText="Yes"
        disagreeText="Cancel"
        onClose={handleCloseDialog}
        onAgree={hanldleAgreePresent}
        noteText={noteText}
        setNoteText={setNoteText}
      />
      <ReusableSnackbar
        open={snackbarOpen.status}
        message={snackbarOpen.message}
        onClose={handleSnackClose}
        severity={snackbarOpen.severity}
      />
    </>
  );
}
